import React, { useState, useEffect } from 'react';
import { db, collection, getDocs, onSnapshot, query, where, limit, handleFirestoreError, OperationType } from '../firebase';
import { Client, ServiceSchedule, Settings } from '../types';
import { X, Calendar, Building2, Printer, FileSpreadsheet, Download, FileText, ChevronRight, Search, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';

interface ServiceScheduleViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultClientId?: string;
}

export const ServiceScheduleViewerModal: React.FC<ServiceScheduleViewerModalProps> = ({
  isOpen,
  onClose,
  defaultClientId = ''
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>(defaultClientId);
  const [schedules, setSchedules] = useState<ServiceSchedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<ServiceSchedule | null>(null);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  // Fetch Settings (letterhead, company name, etc.)
  useEffect(() => {
    if (isOpen) {
      if (defaultClientId) {
        setSelectedClientId(defaultClientId);
      }
    }
  }, [isOpen, defaultClientId]);

  useEffect(() => {
    if (!isOpen) return;
    const unsub = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setSettings({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() } as Settings);
      }
    });
    return () => unsub();
  }, [isOpen]);

  // Fetch Clients
  useEffect(() => {
    if (!isOpen) return;
    const unsub = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const loadedClients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Client));
      setClients(loadedClients);
    });
    return () => unsub();
  }, [isOpen]);

  // Fetch Service Schedules when client selected
  useEffect(() => {
    if (!isOpen || !selectedClientId) {
      setSchedules([]);
      setSelectedSchedule(null);
      return;
    }

    setLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'service_schedules'), where('clientId', '==', selectedClientId)),
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ServiceSchedule));
        setSchedules(docs);
        if (docs.length > 0) {
          setSelectedSchedule(docs[0]);
        } else {
          setSelectedSchedule(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error loading schedules for client:", err);
        handleFirestoreError(err, OperationType.GET, 'service_schedules');
        setLoading(false);
      }
    );

    return () => unsub();
  }, [isOpen, selectedClientId]);

  // Handler 1: PRINT Button
  const handlePrint = (schedule: ServiceSchedule) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print the Service Schedule');
      return;
    }

    const companyName = settings?.companyName || 'BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED';
    const logoUrl = settings?.letterheadUrl || settings?.logoUrl || '';

    const itemsHtml = (schedule.items || []).map((item, idx) => `
      <tr>
        <td style="text-align: center; font-weight: bold;">${item.sn || idx + 1}</td>
        <td style="font-weight: bold;">${item.branchName || '-'}</td>
        <td style="text-align: center; font-weight: bold;">${item.noOfAcs || 0}</td>
        <td style="text-align: center;">${item.noOfDays || 1}</td>
        <td style="text-align: center;">${item.startingDate || '-'}</td>
        <td style="font-weight: 500;">${item.assignedTechnicianNames || '-'}</td>
        <td style="text-align: center; font-weight: bold; color: ${item.status === 'COMPLETED' ? '#16a34a' : item.status === 'IN PROGRESS' ? '#d97706' : '#dc2626'};">
          ${item.status || 'PENDING'}
        </td>
        <td style="text-align: center;">${item.reportReceived || 'NO'}</td>
        <td style="text-align: center;">${item.smemoReceived || 'NO'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Service Schedule - ${schedule.clientName}</title>
          <style>
            @page { size: A4 landscape; margin: 15mm; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; margin: 0; padding: 10px; }
            .header-img { width: 100%; max-height: 120px; object-fit: contain; margin-bottom: 15px; }
            .company-header { text-align: center; background: #1e3a8a; color: white; padding: 10px; margin-bottom: 15px; border-radius: 4px; }
            .title-box { margin-bottom: 15px; border-bottom: 2px solid #1e3a8a; padding-bottom: 8px; }
            .client-title { font-size: 16px; font-weight: bold; color: #1e3a8a; margin: 0 0 4px 0; text-transform: uppercase; }
            .quarter-title { font-size: 13px; font-weight: bold; color: #334155; margin: 0 0 4px 0; text-transform: uppercase; }
            .date-title { font-size: 11px; color: #64748b; margin: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 11px; }
            th { background-color: #1e3a8a; color: white; padding: 8px 6px; border: 1px solid #1e3a8a; font-weight: bold; text-align: center; font-size: 10px; text-transform: uppercase; }
            td { padding: 6px 8px; border: 1px solid #cbd5e1; font-size: 10.5px; }
            tr:nth-child(even) { background-color: #f8fafc; }
            .signatures { margin-top: 35px; display: flex; justify-content: space-between; page-break-inside: avoid; }
            .sig-box { width: 40%; }
            .sig-line { border-top: 1px solid #94a3b8; margin-top: 30px; }
            .sig-text { font-size: 11px; color: #475569; margin-top: 4px; font-weight: bold; }
          </style>
        </head>
        <body>
          ${logoUrl ? `<img src="${logoUrl}" class="header-img" />` : `<div class="company-header"><h2 style="margin:0">${companyName}</h2></div>`}
          
          <div class="title-box">
            <h1 class="client-title">CLIENT: ${schedule.clientName}</h1>
            <h2 class="quarter-title">${schedule.quarterTitle}</h2>
            <p class="date-title">DATE: ${schedule.scheduleDate}</p>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 40px;">S/N</th>
                <th>BRANCHES</th>
                <th style="width: 80px;">NO. OF ACs</th>
                <th style="width: 80px;">NO. OF DAYS</th>
                <th style="width: 100px;">STARTING DATE</th>
                <th>ASSIGNED TECHNICIAN</th>
                <th style="width: 90px;">STATUS</th>
                <th style="width: 90px;">REPORT RECV</th>
                <th style="width: 90px;">MEMO RECV</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>

          <div class="signatures">
            <div class="sig-box">
              <p style="margin:0; font-[11px]; font-weight:bold; color:#1e3a8a;">Prepared By:</p>
              <p style="margin:2px 0; font-size:10px; color:#64748b;">Customer Care / Maintenance Department</p>
              <div class="sig-line"></div>
              <p class="sig-text">Signature & Date</p>
            </div>
            <div class="sig-box">
              <p style="margin:0; font-[11px]; font-weight:bold; color:#1e3a8a;">Approved By:</p>
              <p style="margin:2px 0; font-size:10px; color:#64748b;">Technical Operations Manager</p>
              <div class="sig-line"></div>
              <p class="sig-text">Signature & Date</p>
            </div>
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Handler 2: EXCEL Button
  const handleExportExcel = (schedule: ServiceSchedule) => {
    const excelData = (schedule.items || []).map((item, idx) => ({
      'S/N': item.sn || idx + 1,
      'Client Name': schedule.clientName,
      'Branch Name': item.branchName,
      'Number of ACs': item.noOfAcs || 0,
      'Number of Days': item.noOfDays || 1,
      'Starting Date': item.startingDate,
      'Assigned Technician(s)': item.assignedTechnicianNames || 'Unassigned',
      'Status': item.status || 'PENDING',
      'Report Received': item.reportReceived || 'NO',
      'S/Memo Received': item.smemoReceived || 'NO'
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Auto-width for columns
    const colWidths = [
      { wch: 6 },  // S/N
      { wch: 25 }, // Client
      { wch: 30 }, // Branch
      { wch: 15 }, // ACs
      { wch: 15 }, // Days
      { wch: 16 }, // Start Date
      { wch: 35 }, // Technicians
      { wch: 15 }, // Status
      { wch: 18 }, // Report
      { wch: 18 }  // Memo
    ];
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Service Schedule');

    const fileName = `Service_Schedule_${(schedule.clientName || 'Client').replace(/\s+/g, '_')}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success(`Exported Excel: ${fileName}`);
  };

  // Handler 3: DOWNLOAD (CSV/Text Data File) Button
  const handleDownloadFile = async (schedule: ServiceSchedule) => {
    const headers = ['S/N', 'Client Name', 'Branch Name', 'Number of ACs', 'Number of Days', 'Starting Date', 'Assigned Technicians', 'Status', 'Report Received', 'S/Memo Received'];
    const rows = (schedule.items || []).map((item, idx) => [
      item.sn || idx + 1,
      `"${(schedule.clientName || '').replace(/"/g, '""')}"`,
      `"${(item.branchName || '').replace(/"/g, '""')}"`,
      item.noOfAcs || 0,
      item.noOfDays || 1,
      `"${(item.startingDate || '').replace(/"/g, '""')}"`,
      `"${(item.assignedTechnicianNames || '').replace(/"/g, '""')}"`,
      `"${(item.status || 'PENDING').replace(/"/g, '""')}"`,
      `"${(item.reportReceived || 'NO').replace(/"/g, '""')}"`,
      `"${(item.smemoReceived || 'NO').replace(/"/g, '""')}"`
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = `Service_Schedule_${(schedule.clientName || 'Client').replace(/\s+/g, '_')}.csv`;
    
    await downloadFile(blob, fileName);
    toast.success('Downloaded Service Schedule file.');
  };

  // Handler 4: PDF Button
  const handleExportPDF = (schedule: ServiceSchedule) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let currentY = 15;

    // Company Letterhead Header
    if (settings?.letterheadUrl) {
      try {
        const imgProps = doc.getImageProperties(settings.letterheadUrl);
        const imgWidth = pageWidth - 30;
        const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
        doc.addImage(settings.letterheadUrl, 'PNG', 15, 8, imgWidth, imgHeight);
        currentY = 8 + imgHeight + 8;
      } catch (e) {
        doc.setFillColor(30, 58, 138);
        doc.rect(15, 10, pageWidth - 30, 16, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(settings?.companyName || 'BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 20, { align: 'center' });
        currentY = 34;
      }
    } else {
      doc.setFillColor(30, 58, 138);
      doc.rect(15, 10, pageWidth - 30, 16, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(settings?.companyName || 'BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED', pageWidth / 2, 20, { align: 'center' });
      currentY = 34;
    }

    // Client Name & Schedule Title
    doc.setTextColor(30, 58, 138);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`CLIENT NAME: ${(schedule.clientName || '').toUpperCase()}`, 15, currentY);

    currentY += 6;
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.text(schedule.quarterTitle.toUpperCase(), 15, currentY);

    if (schedule.scheduleDate) {
      currentY += 5;
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(`DATE: ${schedule.scheduleDate.toUpperCase()}`, 15, currentY);
    }

    currentY += 7;

    const tableRows = (schedule.items || []).map((item, index) => [
      item.sn || (index + 1),
      item.branchName || '',
      item.noOfAcs || 0,
      item.noOfDays || 1,
      item.startingDate || '',
      item.assignedTechnicianNames || '',
      item.status || 'PENDING',
      item.reportReceived || 'NO',
      item.smemoReceived || 'NO'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [[
        'S/N',
        'BRANCHES',
        'NUMBER OF ACs',
        'NUMBER OF DAYS',
        'STARTING DATE',
        'ASSIGNED TECHNICIAN',
        'STATUS',
        'REPORT RECEIVED',
        'S/MEMO RECEIVED'
      ]],
      body: tableRows,
      theme: 'grid',
      margin: { left: 15, right: 15 },
      headStyles: {
        fillColor: [30, 58, 138],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: [30, 41, 59],
        valign: 'middle',
        cellPadding: 2
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 12 },
        1: { cellWidth: 55 },
        2: { halign: 'center', cellWidth: 22 },
        3: { halign: 'center', cellWidth: 22 },
        4: { halign: 'center', cellWidth: 28 },
        5: { cellWidth: 55 },
        6: { halign: 'center', cellWidth: 25 },
        7: { halign: 'center', cellWidth: 24 },
        8: { halign: 'center', cellWidth: 24 }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 6) {
          const val = String(data.cell.raw).toUpperCase();
          if (val === 'COMPLETED') {
            data.cell.styles.textColor = [16, 185, 129];
            data.cell.styles.fontStyle = 'bold';
          } else if (val === 'IN PROGRESS') {
            data.cell.styles.textColor = [217, 119, 6];
            data.cell.styles.fontStyle = 'bold';
          } else if (val === 'PENDING') {
            data.cell.styles.textColor = [225, 29, 72];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 12;

    if (finalY + 25 < pageHeight) {
      doc.setFontSize(8.5);
      doc.setTextColor(71, 85, 105);
      doc.setFont('helvetica', 'bold');
      
      doc.text('Prepared By:', 15, finalY);
      doc.text('Approved By:', pageWidth - 80, finalY);
      
      doc.setFont('helvetica', 'normal');
      doc.text('Customer Care / Maintenance Dept.', 15, finalY + 4);
      doc.text('Technical Operations Manager', pageWidth - 80, finalY + 4);

      doc.setDrawColor(203, 213, 225);
      doc.line(15, finalY + 14, 75, finalY + 14);
      doc.line(pageWidth - 80, finalY + 14, pageWidth - 20, finalY + 14);

      doc.setFontSize(7.5);
      doc.text('Signature & Date', 15, finalY + 18);
      doc.text('Signature & Date', pageWidth - 80, finalY + 18);
    }

    const fileName = `Service_Schedule_${(schedule.clientName || 'Client').replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
    toast.success(`Exported PDF: ${fileName}`);
  };

  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.email && c.email.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  const selectedClientObject = clients.find(c => c.id === selectedClientId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        className="relative w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden my-auto max-h-[92vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/10 rounded-2xl border border-white/20 shadow-xs">
              <Calendar className="w-6 h-6 text-blue-200" />
            </div>
            <div>
              <h2 className="text-xl font-black tracking-tight">View Service Schedules</h2>
              <p className="text-xs text-blue-200 font-medium">Select a client to inspect generated service schedules and export with full actions.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 hover:bg-white/10 rounded-2xl transition-all text-white/80 hover:text-white cursor-pointer"
            title="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50/50">
          {/* Client Selection Bar */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3">
            <label className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-900" />
              <span>Step 1: Select Client to View Service Schedules</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  placeholder="Filter clients by name..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/20"
                />
              </div>

              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-800/20 cursor-pointer shadow-xs"
              >
                <option value="">-- Click to Select Client --</option>
                {filteredClients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.email ? `(${c.email})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Schedules Section */}
          {!selectedClientId ? (
            <div className="text-center py-16 bg-white border-2 border-dashed border-slate-200 rounded-3xl space-y-3">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto" />
              <h3 className="text-base font-bold text-slate-700">No Client Selected</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                Please select a client from the dropdown above to view all their generated Air Conditioner Service Schedules.
              </p>
            </div>
          ) : loading ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-slate-200">
              <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p className="text-xs font-bold text-slate-600">Loading service schedules for client...</p>
            </div>
          ) : schedules.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-3xl space-y-3">
              <Calendar className="w-12 h-12 text-slate-300 mx-auto" />
              <h3 className="text-base font-bold text-slate-700">No Service Schedules Found</h3>
              <p className="text-xs text-slate-400 max-w-md mx-auto">
                There are currently no saved service schedules for <span className="font-bold text-slate-800">{selectedClientObject?.name}</span>. You can generate a new schedule in the Customer Care Dashboard or Service Schedules module.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* List of Schedules Tab Buttons if multiple */}
              {schedules.length > 1 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  {schedules.map((sch, idx) => (
                    <button
                      key={sch.id}
                      onClick={() => setSelectedSchedule(sch)}
                      className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer border ${
                        selectedSchedule?.id === sch.id
                          ? 'bg-blue-900 text-white border-blue-900 shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      Schedule #{idx + 1} ({sch.scheduleDate || 'No Date'})
                    </button>
                  ))}
                </div>
              )}

              {/* Active Selected Schedule View */}
              {selectedSchedule && (
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-6">
                  {/* Title & Action Buttons Bar */}
                  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 p-5 bg-gradient-to-r from-blue-50 via-indigo-50/50 to-slate-50 rounded-2xl border border-blue-100">
                    <div>
                      <span className="inline-block px-3 py-1 bg-blue-900 text-white rounded-lg text-[10px] font-black uppercase tracking-wider mb-2">
                        {selectedSchedule.clientName}
                      </span>
                      <h3 className="text-base font-black text-slate-900 leading-snug">
                        {selectedSchedule.quarterTitle}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium mt-1">
                        Schedule Date: <span className="font-bold text-slate-800">{selectedSchedule.scheduleDate || 'N/A'}</span>
                      </p>
                    </div>

                    {/* ACTION BUTTONS: Print, Excel, Download, PDF, Close */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => handlePrint(selectedSchedule)}
                        className="px-3.5 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Print Schedule"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        <span>Print</span>
                      </button>

                      <button
                        onClick={() => handleExportExcel(selectedSchedule)}
                        className="px-3.5 py-2 bg-emerald-700 text-white rounded-xl text-xs font-bold hover:bg-emerald-800 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Export to Excel Spreadsheet"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5" />
                        <span>Excel</span>
                      </button>

                      <button
                        onClick={() => handleDownloadFile(selectedSchedule)}
                        className="px-3.5 py-2 bg-indigo-700 text-white rounded-xl text-xs font-bold hover:bg-indigo-800 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Download CSV / Raw Data"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>Download</span>
                      </button>

                      <button
                        onClick={() => handleExportPDF(selectedSchedule)}
                        className="px-3.5 py-2 bg-rose-700 text-white rounded-xl text-xs font-bold hover:bg-rose-800 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                        title="Export to PDF Document"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>PDF</span>
                      </button>

                      <button
                        onClick={onClose}
                        className="px-3.5 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer"
                        title="Close Viewer"
                      >
                        <X className="w-3.5 h-3.5" />
                        <span>Close</span>
                      </button>
                    </div>
                  </div>

                  {/* Summary Metric Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl text-center">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Branches</p>
                      <p className="text-xl font-black text-slate-800 mt-1">{(selectedSchedule.items || []).length}</p>
                    </div>
                    <div className="p-4 bg-blue-50/60 border border-blue-100 rounded-2xl text-center">
                      <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Total AC Quantity</p>
                      <p className="text-xl font-black text-blue-900 mt-1">
                        {(selectedSchedule.items || []).reduce((sum, item) => sum + (item.noOfAcs || 0), 0)}
                      </p>
                    </div>
                    <div className="p-4 bg-emerald-50/60 border border-emerald-100 rounded-2xl text-center">
                      <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Completed</p>
                      <p className="text-xl font-black text-emerald-900 mt-1">
                        {(selectedSchedule.items || []).filter(i => i.status === 'COMPLETED').length}
                      </p>
                    </div>
                    <div className="p-4 bg-amber-50/60 border border-amber-100 rounded-2xl text-center">
                      <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Pending / In Progress</p>
                      <p className="text-xl font-black text-amber-900 mt-1">
                        {(selectedSchedule.items || []).filter(i => i.status !== 'COMPLETED').length}
                      </p>
                    </div>
                  </div>

                  {/* Schedule Details Table */}
                  <div className="border border-slate-200 rounded-2xl overflow-x-auto shadow-xs">
                    <table className="w-full text-left border-collapse min-w-[900px]">
                      <thead>
                        <tr className="bg-blue-900 text-white text-[11px] uppercase tracking-wider font-bold">
                          <th className="p-3 text-center w-12 border-r border-blue-800">S/N</th>
                          <th className="p-3 border-r border-blue-800">Branch Name</th>
                          <th className="p-3 text-center border-r border-blue-800 w-24">AC Qty</th>
                          <th className="p-3 text-center border-r border-blue-800 w-24">Days</th>
                          <th className="p-3 border-r border-blue-800 w-32">Starting Date</th>
                          <th className="p-3 border-r border-blue-800 min-w-[200px]">Assigned Technician(s)</th>
                          <th className="p-3 text-center border-r border-blue-800 w-32">Status</th>
                          <th className="p-3 text-center border-r border-blue-800 w-24">Report Recv</th>
                          <th className="p-3 text-center w-24">Memo Recv</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 text-xs font-medium">
                        {(selectedSchedule.items || []).map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50 transition-colors">
                            <td className="p-3 text-center font-bold text-slate-500 bg-slate-50/50 border-r border-slate-200">{item.sn || idx + 1}</td>
                            <td className="p-3 font-bold text-slate-800 border-r border-slate-200">{item.branchName || '-'}</td>
                            <td className="p-3 text-center font-bold text-blue-900 border-r border-slate-200">{item.noOfAcs || 0}</td>
                            <td className="p-3 text-center text-slate-600 border-r border-slate-200">{item.noOfDays || 1}</td>
                            <td className="p-3 text-slate-700 border-r border-slate-200">{item.startingDate || '-'}</td>
                            <td className="p-3 text-slate-800 border-r border-slate-200">
                              {item.assignedTechnicianNames ? (
                                <span className="font-semibold text-blue-900 bg-blue-50 px-2 py-1 rounded-md text-[11px]">
                                  {item.assignedTechnicianNames}
                                </span>
                              ) : (
                                <span className="text-slate-400 italic">Unassigned</span>
                              )}
                            </td>
                            <td className="p-3 text-center border-r border-slate-200">
                              <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider inline-block ${
                                item.status === 'COMPLETED' ? 'bg-emerald-100 text-emerald-800' :
                                item.status === 'IN PROGRESS' ? 'bg-amber-100 text-amber-800' :
                                'bg-rose-100 text-rose-800'
                              }`}>
                                {item.status || 'PENDING'}
                              </span>
                            </td>
                            <td className="p-3 text-center font-bold text-slate-600 border-r border-slate-200">{item.reportReceived || 'NO'}</td>
                            <td className="p-3 text-center font-bold text-slate-600">{item.smemoReceived || 'NO'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-slate-100 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>Service Schedules Module</span>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-700 transition-all cursor-pointer"
          >
            Close Window
          </button>
        </div>
      </motion.div>
    </div>
  );
};
